/**
 * SignatureCapture Component
 *
 * Provides dual-mode signature capture:
 * 1. Topaz SigPlusExtLite v3 - For users with signature pads
 * 2. Canvas Fallback - For testing or when no pad available
 *
 * Usage:
 * <SignatureCapture
 *   label="Client Signature"
 *   signatureRole="client"
 *   width={400}
 *   height={150}
 *   existingSignature={null}
 *   onCapture={(sigData) => {}}
 * />
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import TypedSignature from './TypedSignature';

const IS_DEV = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (IS_DEV) console.log('[SignatureCapture]', ...args); };

// Topaz SigPlusExtLite v3 SDK wrapper
const TopazSDK = {
  isExtensionInstalled: false,
  isInitialized: false,
  wrapperLoaded: false,

  async initialize() {
    if (this.isInitialized) {
      return this.isExtensionInstalled;
    }

    return new Promise((resolve) => {
      // Check if the SigPlusExtLite extension is installed by looking for the marker attribute
      const extensionInstalled = document.documentElement.getAttribute('SigPlusExtLiteExtension-installed');

      if (extensionInstalled === 'yes' || extensionInstalled === 'true') {
        devLog('SigPlusExtLite extension detected');
        this.isExtensionInstalled = true;

        // Load the wrapper script from the extension
        this.loadWrapper().then(() => {
          this.isInitialized = true;
          resolve(true);
        }).catch((err) => {
          devLog('Failed to load wrapper:', err);
          this.isInitialized = true;
          resolve(false);
        });
      } else {
        devLog('SigPlusExtLite extension not detected');
        this.isExtensionInstalled = false;
        this.isInitialized = true;
        resolve(false);
      }

      // Timeout after 3 seconds
      setTimeout(() => {
        if (!this.isInitialized) {
          devLog('Topaz detection timeout');
          this.isInitialized = true;
          resolve(this.isExtensionInstalled);
        }
      }, 3000);
    });
  },

  async loadWrapper() {
    if (this.wrapperLoaded) return;

    return new Promise((resolve, reject) => {
      // Get the wrapper URL from the extension's content script attribute
      const wrapperUrl = document.documentElement.getAttribute('SigPlusExtLiteWrapperURL');

      if (!wrapperUrl) {
        devLog('Wrapper URL not found, extension may not be fully loaded');
        reject(new Error('Wrapper URL not available'));
        return;
      }

      devLog('Loading SigPlusExtLite wrapper from:', wrapperUrl);

      const script = document.createElement('script');
      script.src = wrapperUrl;
      script.onload = () => {
        devLog('SigPlusExtLite wrapper loaded successfully');
        this.wrapperLoaded = true;
        resolve();
      };
      script.onerror = () => {
        devLog('Failed to load SigPlusExtLite wrapper');
        reject(new Error('Failed to load wrapper script'));
      };
      document.head.appendChild(script);
    });
  },

  async captureSignature(options = {}) {
    const { width = 500, height = 150 } = options;

    // Check if Topaz global is available
    if (typeof window.Topaz === 'undefined') {
      throw new Error('Topaz SDK not loaded. Please ensure the extension is installed and refresh the page.');
    }

    return new Promise(async (resolve, reject) => {
      try {
        devLog('Starting Topaz signature capture');

        const Sign = window.Topaz.SignatureCaptureWindow.Sign;

        // Start the signature capture (use defaults for image settings)
        await Sign.StartSign();

        // Check if signed
        const isSigned = await Sign.IsSigned();
        devLog('IsSigned result:', isSigned);

        if (isSigned) {
          // Get the signature image as base64
          const imageData = await Sign.GetSignatureImage();
          const sigString = await Sign.GetSigString();

          if (imageData && imageData.length > 0) {
            resolve({
              imageBase64: `data:image/png;base64,${imageData}`,
              sigString: sigString,
              timestamp: new Date().toISOString(),
            });
          } else {
            reject(new Error('No signature data received'));
          }
        } else {
          reject(new Error('Signature was cancelled'));
        }
      } catch (err) {
        devLog('Topaz capture error:', err);
        reject(err);
      }
    });
  },

  async clearSignature() {
    if (typeof window.Topaz !== 'undefined' && window.Topaz.SignatureCaptureWindow) {
      try {
        await window.Topaz.SignatureCaptureWindow.Sign.ClearTablet();
      } catch (e) {
        devLog('Error clearing tablet:', e);
      }
    }
  },

  async getVersionInfo() {
    if (typeof window.Topaz === 'undefined') return null;

    try {
      const extVersion = await window.Topaz.Global.GetSigPlusExtLiteVersion();
      const nmhVersion = await window.Topaz.Global.GetSigPlusExtLiteNMHVersion();
      return { extVersion, nmhVersion };
    } catch (e) {
      devLog('Error getting version info:', e);
      return null;
    }
  }
};

function SignatureCapture({
  onCapture,
  signatureRole = 'client',
  label = 'Signature',
  width = 400,
  height = 150,
  existingSignature = null,
  disabled = false,
  showSetupLink = true,
  captureMode = 'full',  // 'full' (Topaz + draw) or 'client' (draw + type)
}) {
  const [mode, setMode] = useState(captureMode === 'client' ? 'canvas' : 'detecting'); // detecting, topaz, canvas, typed, preview
  const [clientTab, setClientTab] = useState('draw'); // draw, type (only used in client mode)
  const [signatureData, setSignatureData] = useState(existingSignature);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [topazAvailable, setTopazAvailable] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  // Initialize and detect Topaz
  useEffect(() => {
    const detectTopaz = async () => {
      if (existingSignature) {
        setSignatureData(existingSignature);
        setMode('preview');
        return;
      }

      // Client mode: skip Topaz entirely, use canvas/typed
      if (captureMode === 'client') {
        setMode('canvas');
        devLog('Client mode — using canvas/typed');
        return;
      }

      try {
        // Wait a moment for extension to inject its attributes
        await new Promise(r => setTimeout(r, 500));

        const hasTopaz = await TopazSDK.initialize();
        setTopazAvailable(hasTopaz);

        if (hasTopaz) {
          setMode('topaz');
          devLog('Topaz signature pad detected and ready');

          // Log version info for debugging
          const versionInfo = await TopazSDK.getVersionInfo();
          if (versionInfo) {
            devLog('Topaz versions:', versionInfo);
          }
        } else {
          setMode('canvas');
          devLog('Using canvas fallback');
        }
      } catch (err) {
        devLog('Error detecting Topaz:', err);
        setMode('canvas');
      }
    };

    detectTopaz();
  }, [existingSignature, captureMode]);

  // Initialize canvas when in canvas mode
  useEffect(() => {
    if (mode === 'canvas' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [mode]);

  // Canvas drawing handlers
  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const touch = e.touches?.[0];
    return {
      x: ((touch?.clientX || e.clientX) - rect.left) * scaleX,
      y: ((touch?.clientY || e.clientY) - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    if (disabled) return;
    isDrawing.current = true;
    lastPos.current = getCanvasCoords(e);
  }, [getCanvasCoords, disabled]);

  const draw = useCallback((e) => {
    if (!isDrawing.current || disabled) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const pos = getCanvasCoords(e);

    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    lastPos.current = pos;
  }, [getCanvasCoords, disabled]);

  const stopDrawing = useCallback(() => {
    isDrawing.current = false;
  }, []);

  // Capture from Topaz pad
  const captureFromTopaz = async () => {
    if (disabled) return;
    setIsCapturing(true);
    setError(null);

    try {
      const result = await TopazSDK.captureSignature({ width, height });
      setSignatureData(result);
      setMode('preview');
      onCapture?.({
        role: signatureRole,
        ...result,
      });
    } catch (err) {
      setError(err.message || 'Failed to capture signature');
      devLog('Topaz capture error:', err);
    } finally {
      setIsCapturing(false);
    }
  };

  // Capture from canvas
  const captureFromCanvas = useCallback(() => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Check if canvas has content (not just white background)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let hasContent = false;

    for (let i = 0; i < imageData.data.length; i += 4) {
      // Check if any pixel is not white
      if (imageData.data[i] < 250 || imageData.data[i + 1] < 250 || imageData.data[i + 2] < 250) {
        hasContent = true;
        break;
      }
    }

    if (!hasContent) {
      setError('Please sign before capturing');
      return;
    }

    const result = {
      imageBase64: canvas.toDataURL('image/png'),
      timestamp: new Date().toISOString(),
    };

    setSignatureData(result);
    setMode('preview');
    onCapture?.({
      role: signatureRole,
      ...result,
    });
  }, [signatureRole, onCapture, disabled]);

  // Clear signature
  const clearSignature = useCallback(() => {
    if (disabled) return;

    TopazSDK.clearSignature();

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }

    setSignatureData(null);
    setError(null);
    if (captureMode === 'client') {
      setMode('canvas');
      setClientTab('draw');
    } else {
      setMode(topazAvailable ? 'topaz' : 'canvas');
    }

    onCapture?.({
      role: signatureRole,
      imageBase64: null,
      timestamp: null,
    });
  }, [topazAvailable, signatureRole, onCapture, disabled]);

  // Render based on mode
  if (mode === 'detecting') {
    return (
      <div className="border rounded p-4 bg-gray-50">
        <label className="block text-sm font-medium mb-2">{label}</label>
        <p className="text-gray-500 text-sm">Detecting signature device...</p>
      </div>
    );
  }

  if (mode === 'preview' && signatureData) {
    return (
      <div className="border rounded p-4">
        <label className="block text-sm font-medium mb-2">{label}</label>
        <div className="border bg-white p-2" style={{ width, maxWidth: '100%' }}>
          <img
            src={signatureData.imageBase64}
            alt={`${signatureRole} signature`}
            className="max-w-full h-auto"
            style={{ maxHeight: height }}
          />
        </div>
        <div className="mt-2 flex items-center gap-4">
          {!disabled && (
            <button
              onClick={clearSignature}
              className="text-red-600 text-sm hover:underline"
            >
              Clear & Re-sign
            </button>
          )}
          <span className="text-xs text-gray-500">
            Signed: {new Date(signatureData.timestamp).toLocaleString()}
          </span>
        </div>
      </div>
    );
  }

  // Handle typed signature capture
  const handleTypedCapture = (sigData) => {
    setSignatureData(sigData);
    setMode('preview');
    onCapture?.({
      role: signatureRole,
      ...sigData,
    });
  };

  // Client mode: Draw / Type tabs
  if (captureMode === 'client' && mode !== 'preview' && mode !== 'detecting') {
    return (
      <div className="border rounded p-4">
        <label className="block text-sm font-medium mb-2">{label}</label>

        {error && (
          <div className="mb-2 text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
        )}

        {/* Draw / Type toggle */}
        <div className="flex mb-3 border-b">
          <button
            onClick={() => setClientTab('draw')}
            className={`px-4 py-2 text-sm font-medium -mb-px ${
              clientTab === 'draw'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Draw
          </button>
          <button
            onClick={() => setClientTab('type')}
            className={`px-4 py-2 text-sm font-medium -mb-px ${
              clientTab === 'type'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Type
          </button>
        </div>

        {clientTab === 'draw' ? (
          <div>
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              className={`border-2 border-dashed border-gray-300 bg-white rounded touch-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}`}
              style={{ width: Math.min(width, 400), height }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            <p className="text-xs text-gray-500 mt-1">Sign using your mouse or finger</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={captureFromCanvas}
                disabled={disabled}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Capture Signature
              </button>
              <button
                onClick={clearSignature}
                disabled={disabled}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <TypedSignature onCapture={handleTypedCapture} width={width} height={height} />
        )}
      </div>
    );
  }

  return (
    <div className="border rounded p-4">
      <label className="block text-sm font-medium mb-2">{label}</label>

      {error && (
        <div className="mb-2 text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
      )}

      {mode === 'topaz' ? (
        // Topaz mode
        <div>
          <div
            className="border-2 border-dashed border-blue-300 bg-blue-50 flex items-center justify-center rounded"
            style={{ width: Math.min(width, 400), height }}
          >
            {isCapturing ? (
              <div className="text-center">
                <p className="text-blue-600 font-medium">Sign on the Topaz pad now...</p>
                <p className="text-gray-500 text-sm mt-1">Click "Done" on the pad when finished</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-blue-600">Topaz Signature Pad Ready</p>
                <p className="text-gray-500 text-xs mt-1">Click "Start Capture" to begin</p>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={captureFromTopaz}
              disabled={isCapturing || disabled}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isCapturing ? 'Waiting for signature...' : 'Start Capture'}
            </button>
            <button
              onClick={() => setMode('canvas')}
              disabled={disabled}
              className="text-gray-600 text-sm hover:underline disabled:opacity-50"
            >
              Use mouse/touch instead
            </button>
          </div>
        </div>
      ) : (
        // Canvas fallback mode
        <div>
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className={`border-2 border-dashed border-gray-300 bg-white rounded touch-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}`}
            style={{ width: Math.min(width, 400), height }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <p className="text-xs text-gray-500 mt-1">
            Sign using your mouse or finger (on touch devices)
            {!topazAvailable && showSetupLink && (
              <span className="ml-2">
                | <a
                  href="/TopazSetup.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Set up Topaz signature pad
                </a>
              </span>
            )}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={captureFromCanvas}
              disabled={disabled}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Capture Signature
            </button>
            <button
              onClick={clearSignature}
              disabled={disabled}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            {topazAvailable && (
              <button
                onClick={() => setMode('topaz')}
                disabled={disabled}
                className="text-blue-600 text-sm hover:underline disabled:opacity-50"
              >
                Use Topaz pad
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SignatureCapture;
